import type { ComponentType } from 'react';
import type { MDXComponents } from 'mdx/types';
import { Badge } from '../../components/Badge';
import { Link } from '../../components/Link';
import { Eyebrow } from '../../components/Eyebrow';
import { mdxComponents } from '../../components/mdx/components';
import { BRAND_PROVIDER } from '../../lib/brand';
import { getAuthor, authorUrl } from '../../lib/blog/authors';
import type { BlogFrontmatter } from '../../lib/blog/meta';
import { fmtUtcDate } from '../../lib/format';
import { serializeJsonLd } from '../../lib/jsonLd';
import { breadcrumbList } from '../../lib/seo/breadcrumb';
import { ORG_ID } from '../../lib/seo/orgSchema';
import { Band } from '../../components/editorial/Band';
import { FunnelBanner } from '../../components/editorial/FunnelBanner';
import { BlogToc } from './BlogToc';

interface EditorialPost {
  slug: string;
  frontmatter: BlogFrontmatter;
  Component: ComponentType<{ components?: MDXComponents }>;
}

interface EditorialSection {
  name: string;
  href: '/blog' | '/alternatives';
  url: string;
}

function schemaGraph(post: EditorialPost, canonicalUrl: string, section: EditorialSection) {
  const fm = post.frontmatter;
  const author = getAuthor(fm.author);
  const graph: object[] = [
    {
      '@type': 'BlogPosting',
      '@id': `${canonicalUrl}#blogposting`,
      headline: fm.title,
      description: fm.description,
      datePublished: fm.date,
      dateModified: fm.dateModified ?? fm.date,
      author: author
        ? { '@id': `${authorUrl(author.slug)}#person` }
        : { '@type': 'Organization', name: fm.author ?? BRAND_PROVIDER },
      publisher: { '@id': ORG_ID },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
      ...(fm.ogImage ? { image: new URL(fm.ogImage, 'https://getanyapi.com').href } : {}),
    },
    breadcrumbList(
      { name: section.name, url: section.url },
      { name: fm.title, url: canonicalUrl },
    ),
  ];
  if (author) {
    graph.push({
      '@type': 'Person',
      '@id': `${authorUrl(author.slug)}#person`,
      name: author.name,
      jobTitle: author.title,
      image: `https://getanyapi.com${author.photo}`,
      url: authorUrl(author.slug),
      worksFor: { '@id': ORG_ID },
      sameAs: author.sameAs,
      knowsAbout: author.knowsAbout,
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

export function EditorialPostPage({
  post,
  canonicalUrl,
  section,
  heroAlt,
}: {
  post: EditorialPost;
  canonicalUrl: string;
  section: EditorialSection;
  heroAlt?: string;
}) {
  const { frontmatter: fm, Component } = post;
  const author = getAuthor(fm.author);
  const modified = fm.dateModified && fm.dateModified !== fm.date;
  return (
    <>
      <Band>
        <div className="band lg:flex lg:gap-12 xl:px-24">
          <aside className="order-first mb-8 hidden lg:block lg:mb-0 lg:w-50 lg:flex-none">
            <div className="lg:sticky lg:top-21"><BlogToc /></div>
          </aside>
          <article className="mx-auto min-w-0 max-w-180">
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: serializeJsonLd(schemaGraph(post, canonicalUrl, section)) }}
            />
            <Eyebrow to={section.href}>{section.name}</Eyebrow>
            <header className="mb-7 mt-3">
              <h1 className="display-2 text-text">{fm.title}</h1>
              {author ? (
                <div className="mt-4 flex items-center gap-3">
                  <Link to={`/blog/author/${author.slug}`} className="flex-none">
                    <img
                      src={author.photo}
                      alt={author.name}
                      width={40}
                      height={40}
                      loading="lazy"
                      className="h-10 w-10 rounded-full border border-rule object-cover"
                    />
                  </Link>
                  <div className="leading-tight">
                    <Link to={`/blog/author/${author.slug}`} className="text-14 font-medium text-text hover:text-accent">
                      {author.name}
                    </Link>
                    <div className="mt-0.5 text-13 text-text-3">
                      {author.title} · {fmtUtcDate(fm.date, 'long')}
                      {modified ? ` · Updated ${fmtUtcDate(fm.dateModified!, 'long')}` : ''}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-3 text-13 text-text-3">
                  {fmtUtcDate(fm.date, 'long')} · {fm.author ?? BRAND_PROVIDER}
                </div>
              )}
              {fm.tags?.length ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {fm.tags.map((tag) => <Badge key={tag}>{tag}</Badge>)}
                </div>
              ) : null}
            </header>
            {fm.ogImage ? (
              <div className="mb-8 overflow-hidden rounded-12 bg-tile">
                <img
                  src={fm.ogImage}
                  alt={heroAlt ?? fm.title}
                  width={1200}
                  height={630}
                  className="w-full"
                />
              </div>
            ) : null}
            <div className="mb-8 lg:hidden"><BlogToc variant="inline" /></div>
            <div className="blog-prose"><Component components={mdxComponents} /></div>
          </article>
        </div>
      </Band>
      <FunnelBanner heading="Try the API the post used." />
    </>
  );
}
